import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminClinicsListTemplate, LoadingTemplate } from '@/components/templates';
import OrderRezeptSheetsFilterPanel from '@/components/organisms/OrderRezeptSheetsFilterPanel';
import OrderRezeptSheetsTableSection from '@/components/organisms/OrderRezeptSheetsTableSection';
import {
  useAdminOrderRezeptSheetsList,
  useUpdateShipDateMutation,
} from '@/hooks/admin/useAdminOrderRezeptSheets';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants';
import type { OrderRezeptSheetListParams } from '@/services/admin/order-rezept-sheets';
import { downloadOrderRezeptSheetsCsv } from '@/services/admin/order-rezept-sheets';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type FilterState = {
  siteTypes: string[];
  serviceType: string;
  orderDateFrom: string;
  orderDateTo: string;
  incDelivered: boolean;
};

type EditedDetail = {
  ship_date: string | null;
  remarks: string | null;
};

const getFiltersFromSearchParams = (sp: URLSearchParams): FilterState => {
  const siteTypesArr = sp.getAll('site_types').filter(Boolean);
  const siteTypesCsv = (sp.get('site_types') ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesArr.length > 0 ? siteTypesArr : siteTypesCsv;

  return {
    siteTypes,
    serviceType: sp.get('service_type') ?? '',
    orderDateFrom: sp.get('order_date_from') ?? '',
    orderDateTo: sp.get('order_date_to') ?? '',
    incDelivered: sp.get('inc_delivered') === '1',
  };
};

const getPageFromSearchParams = (sp: URLSearchParams): number => {
  const raw = Number(sp.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const OrderRezeptSheetsPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  // Tenant site types
  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (site ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] = siteTypesQuery.data?.data?.site_types ?? [];
  const showSiteFilter = tenantSiteTypeList.length > 1;

  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  const tenantSiteTypes = useMemo(
    () => tenantSiteTypeList.map(t => t.site_type).filter(Boolean) as string[],
    [tenantSiteTypeList]
  );

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  // Filter state
  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [serviceType, setServiceType] = useState(initialFilters.serviceType);
  const [orderDateFrom, setOrderDateFrom] = useState(initialFilters.orderDateFrom);
  const [orderDateTo, setOrderDateTo] = useState(initialFilters.orderDateTo);
  const [incDelivered, setIncDelivered] = useState(initialFilters.incDelivered);

  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [appliedServiceType, setAppliedServiceType] = useState(initialFilters.serviceType);
  const [appliedOrderDateFrom, setAppliedOrderDateFrom] = useState(initialFilters.orderDateFrom);
  const [appliedOrderDateTo, setAppliedOrderDateTo] = useState(initialFilters.orderDateTo);
  const [appliedIncDelivered, setAppliedIncDelivered] = useState(initialFilters.incDelivered);

  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => siteTypes.includes(st));

  const [page, setPage] = useState(getPageFromSearchParams(searchParams));

  // Edited details tracking
  const [editedDetails, setEditedDetails] = useState<Map<number, EditedDetail>>(new Map());
  const hasUnsavedChanges = editedDetails.size > 0;

  useEffect(() => {
    const nextFilters = getFiltersFromSearchParams(searchParams);
    setSiteTypes(nextFilters.siteTypes);
    setServiceType(nextFilters.serviceType);
    setOrderDateFrom(nextFilters.orderDateFrom);
    setOrderDateTo(nextFilters.orderDateTo);
    setIncDelivered(nextFilters.incDelivered);
    setAppliedSiteTypes(nextFilters.siteTypes);
    setAppliedServiceType(nextFilters.serviceType);
    setAppliedOrderDateFrom(nextFilters.orderDateFrom);
    setAppliedOrderDateTo(nextFilters.orderDateTo);
    setAppliedIncDelivered(nextFilters.incDelivered);
    setPage(getPageFromSearchParams(searchParams));
  }, [searchParams]);

  // API params
  const params: OrderRezeptSheetListParams = useMemo(
    () => ({
      site_type: showSiteFilter && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      service_type: appliedServiceType || undefined,
      order_date_from: appliedOrderDateFrom || undefined,
      order_date_to: appliedOrderDateTo || undefined,
      inc_delivered: appliedIncDelivered || undefined,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [
      appliedSiteTypes,
      appliedServiceType,
      appliedOrderDateFrom,
      appliedOrderDateTo,
      appliedIncDelivered,
      showSiteFilter,
      page,
    ]
  );

  const { data, isLoading, isFetching } = useAdminOrderRezeptSheetsList(site ?? null, params);

  const innerData = data?.data;
  const rows = innerData?.data ?? [];
  const meta = innerData?.meta
    ? {
        current_page: innerData.meta.current_page,
        last_page: innerData.meta.last_page,
        per_page: innerData.meta.per_page,
        total: innerData.meta.total,
      }
    : { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 };

  // Search
  const doSearch = useCallback(() => {
    const nextParams = new URLSearchParams();
    if (showSiteFilter) {
      siteTypes
        .map(v => v.trim())
        .filter(Boolean)
        .forEach(v => nextParams.append('site_types', v));
    }
    if (serviceType) nextParams.set('service_type', serviceType);
    if (orderDateFrom) nextParams.set('order_date_from', orderDateFrom);
    if (orderDateTo) nextParams.set('order_date_to', orderDateTo);
    if (incDelivered) nextParams.set('inc_delivered', '1');
    nextParams.set('page', '1');
    setSearchParams(nextParams);
    setEditedDetails(new Map());
  }, [showSiteFilter, siteTypes, serviceType, orderDateFrom, orderDateTo, incDelivered, setSearchParams]);

  const handleSearch = doSearch;

  // CSV
  const [csvLoading, setCsvLoading] = useState(false);
  const handleCsv = useCallback(async () => {
    if (!site) return;
    setCsvLoading(true);
    try {
      await downloadOrderRezeptSheetsCsv(site, params);
      toast.success(CSV_SUCCESS_MESSAGE);
    } catch {
      toast.error(CSV_ERROR_MESSAGE);
    } finally {
      setCsvLoading(false);
    }
  }, [site, params, toast]);

  // Edit detail inline
  const handleEditDetail = useCallback(
    (detailId: number, field: 'ship_date' | 'remarks', value: string | null) => {
      setEditedDetails(prev => {
        const next = new Map(prev);

        // Find original value from rows
        let originalShipDate: string | null = null;
        let originalRemarks: string | null = null;
        for (const order of rows) {
          const detail = order.details?.find(d => d.id === detailId);
          if (detail) {
            originalShipDate = detail.ship_date;
            originalRemarks = detail.remarks;
            break;
          }
        }

        // Initialize from original values if not yet edited
        const existing = next.get(detailId) ?? {
          ship_date: originalShipDate,
          remarks: originalRemarks,
        };

        const updated = { ...existing, [field]: value };

        // If both fields match original, remove from edited map
        const shipDateMatches =
          (updated.ship_date ?? null) === (originalShipDate ?? null) ||
          (updated.ship_date === '' && originalShipDate === null);
        const remarksMatches =
          (updated.remarks ?? null) === (originalRemarks ?? null) ||
          (updated.remarks === '' && originalRemarks === null);

        if (shipDateMatches && remarksMatches) {
          next.delete(detailId);
        } else {
          next.set(detailId, updated);
        }

        return next;
      });
    },
    [rows]
  );

  // Ship date bulk update
  const { updateShipDateMutation, isLoading: isUpdating } = useUpdateShipDateMutation(site ?? null);

  const handleUpdateShipDate = useCallback(() => {
    // Only send details that were actually edited
    const changedDetails: { id: number; ship_date: string | null; remarks: string | null }[] = [];
    for (const [detailId, edited] of editedDetails) {
      // ship_date is already stored as YYYY-MM-DD from DatePicker onChange
      changedDetails.push({
        id: detailId,
        ship_date: edited.ship_date,
        remarks: edited.remarks,
      });
    }

    if (changedDetails.length === 0) return;

    updateShipDateMutation({ details: changedDetails }).then(response => {
      if (response?.success) {
        setEditedDetails(new Map());
      }
    });
  }, [editedDetails, updateShipDateMutation]);

  // Page change with unsaved changes check
  const doChangePage = useCallback(
    (nextPage: number) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('page', String(nextPage));
      setSearchParams(nextParams);
      setEditedDetails(new Map());
    },
    [searchParams, setSearchParams]
  );

  const handleChangePage = useCallback(
    (nextPage: number) => {
      if (hasUnsavedChanges) {
        if (window.confirm('編集した内容が保存されていません。編集内容を破棄してもよろしいですか？')) {
          doChangePage(nextPage);
        }
      } else {
        doChangePage(nextPage);
      }
    },
    [hasUnsavedChanges, doChangePage]
  );

  if (isAuthLoading) return <LoadingTemplate />;

  return (
    <AdminClinicsListTemplate
      breadcrumbItems={[{ title: 'レセプト用紙受注状況' }]}
      title="レセプト用紙受注状況"
      filter={
        <OrderRezeptSheetsFilterPanel
          tenants={tenantSiteTypeList}
          showSiteFilter={showSiteFilter}
          siteTypes={siteTypes}
          hasAllSelected={hasAllSelected}
          onChangeSiteTypes={setSiteTypes}
          serviceType={serviceType}
          onChangeServiceType={setServiceType}
          orderDateFrom={orderDateFrom}
          onChangeOrderDateFrom={setOrderDateFrom}
          orderDateTo={orderDateTo}
          onChangeOrderDateTo={setOrderDateTo}
          incDelivered={incDelivered}
          onChangeIncDelivered={setIncDelivered}
          onSearch={handleSearch}
          onCsv={handleCsv}
          csvLoading={csvLoading}
        />
      }
      table={
        <OrderRezeptSheetsTableSection
          rows={rows}
          loading={isLoading || isFetching}
          meta={meta}
          defaultPerPage={DEFAULT_PER_PAGE}
          canUseSiteTypeFeatures={showSiteFilter}
          tenantNameBySiteType={tenantNameBySiteType}
          editedDetails={editedDetails}
          onEditDetail={handleEditDetail}
          onChangePage={handleChangePage}
          onUpdateShipDate={handleUpdateShipDate}
          isUpdating={isUpdating}
        />
      }
    />
  );
};

export default OrderRezeptSheetsPage;
